import { useEffect, useMemo, useRef, useState } from 'react';
import type { GameSnapshot } from '@hypevault/hype-deal';
import { DEFAULT_GAME_SOUND_CONFIG } from '@hypevault/shared-types';
import { gameSounds } from '../lib/gameSounds';

/**
 * Hype Deal overlay — Phase 2 (animations + sound).
 *
 * Renders:
 *   - idle splash when no game is running
 *   - 26-case grid + side value board during play (with case-flip + strike-through)
 *   - banker offer splash with phone-ring shake during banker_offer phase
 *   - deal/no-deal vote bar during deal_voting
 *   - final result splash with money rain on DEAL, confetti on big wins
 */

interface Fields {
  position?: 'fullscreen';
  scale?: number;
  opacity?: number;
}

interface Props {
  streamerId: string;
  snapshot: GameSnapshot | null;
  fields: Fields;
}

const PALETTE = {
  bg: '#040714',
  bgPanel: 'rgba(15, 23, 60, 0.85)',
  gold: '#f5b942',
  red: '#ef4444',
  green: '#22c55e',
  text: '#fff',
  textDim: 'rgba(255,255,255,0.7)',
  caseClosed: 'linear-gradient(160deg, #fcd34d 0%, #d97706 50%, #92400e 100%)',
  caseOpen: 'rgba(255,255,255,0.05)',
  casePlayer: 'linear-gradient(160deg, #93c5fd 0%, #3b82f6 50%, #1e3a8a 100%)',
};

const ANIMATIONS = `
@keyframes hd-fadeIn { from { opacity: 0 } to { opacity: 1 } }
@keyframes hd-pulse { 0%,100% { transform: scale(1) } 50% { transform: scale(1.04) } }
@keyframes hd-shake { 0%,100% { transform: translateX(0) rotate(0) } 15% { transform: translateX(-12px) rotate(-2deg) } 30% { transform: translateX(10px) rotate(2deg) } 45% { transform: translateX(-8px) rotate(-1deg) } 60% { transform: translateX(6px) rotate(1deg) } 75% { transform: translateX(-3px) rotate(0) } }
@keyframes hd-flip {
  0% { transform: rotateY(0deg) scale(1); }
  40% { transform: rotateY(180deg) scale(1.12); }
  60% { transform: rotateY(180deg) scale(1.12); }
  100% { transform: rotateY(360deg) scale(1); }
}
@keyframes hd-revealValue { 0% { opacity: 0; transform: scale(0.6); } 100% { opacity: 1; transform: scale(1); } }
@keyframes hd-strike { 0% { width: 0; } 100% { width: 100%; } }
@keyframes hd-glow {
  0%,100% { box-shadow: 0 0 28px 4px rgba(245,185,66,0.4); }
  50% { box-shadow: 0 0 64px 16px rgba(245,185,66,0.95); }
}
@keyframes hd-rain {
  0% { transform: translateY(-80px) rotateZ(0deg); opacity: 0; }
  10% { opacity: 1; }
  100% { transform: translateY(1180px) rotateZ(720deg); opacity: 0; }
}
@keyframes hd-confetti {
  0% { transform: translateY(-20px) rotate(0); opacity: 1 }
  100% { transform: translateY(1080px) rotate(720deg); opacity: 0 }
}
@keyframes hd-slowReveal {
  0% { transform: rotateY(0); }
  100% { transform: rotateY(180deg) scale(1.2); }
}
`;

// ─── Web Audio synthesized sounds ─────────────────────────────────────────
// We can't ship audio files in this codebase, so generate tones at runtime.
// All sounds are short percussive synth — kept tasteful, low gain.

type SoundName = 'phone-ring' | 'case-open' | 'deal' | 'no-deal' | 'game-over-win' | 'idle-pop';

function makeSoundPlayer() {
  let ctx: AudioContext | null = null;
  function ensure(): AudioContext | null {
    if (typeof window === 'undefined') return null;
    if (!ctx) {
      const AC =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!AC) return null;
      ctx = new AC();
    }
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx;
  }
  function tone(opts: {
    freq: number;
    durationMs: number;
    type?: OscillatorType;
    gain?: number;
    attackMs?: number;
    releaseMs?: number;
    startOffsetMs?: number;
  }) {
    const c = ensure();
    if (!c) return;
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = opts.type ?? 'sine';
    osc.frequency.value = opts.freq;
    osc.connect(g);
    g.connect(c.destination);
    const start = c.currentTime + (opts.startOffsetMs ?? 0) / 1000;
    const attack = (opts.attackMs ?? 8) / 1000;
    const release = (opts.releaseMs ?? 40) / 1000;
    const peak = opts.gain ?? 0.12;
    g.gain.setValueAtTime(0, start);
    g.gain.linearRampToValueAtTime(peak, start + attack);
    g.gain.linearRampToValueAtTime(0.0001, start + opts.durationMs / 1000 + release);
    osc.start(start);
    osc.stop(start + opts.durationMs / 1000 + release + 0.05);
  }
  return {
    play(name: SoundName) {
      switch (name) {
        case 'phone-ring': {
          // Two-tone ring repeated.
          for (let r = 0; r < 2; r++) {
            const base = r * 600;
            tone({ freq: 880, durationMs: 180, type: 'square', gain: 0.08, startOffsetMs: base });
            tone({
              freq: 660,
              durationMs: 180,
              type: 'square',
              gain: 0.08,
              startOffsetMs: base + 200,
            });
          }
          return;
        }
        case 'case-open':
          tone({ freq: 700, durationMs: 60, type: 'triangle', gain: 0.1 });
          tone({ freq: 1200, durationMs: 120, type: 'sine', gain: 0.08, startOffsetMs: 80 });
          return;
        case 'deal':
          tone({ freq: 523, durationMs: 140, type: 'sine', gain: 0.16 });
          tone({ freq: 659, durationMs: 140, type: 'sine', gain: 0.16, startOffsetMs: 140 });
          tone({ freq: 784, durationMs: 240, type: 'sine', gain: 0.18, startOffsetMs: 280 });
          return;
        case 'no-deal':
          tone({ freq: 260, durationMs: 220, type: 'sawtooth', gain: 0.12 });
          tone({ freq: 200, durationMs: 320, type: 'sawtooth', gain: 0.12, startOffsetMs: 200 });
          return;
        case 'game-over-win':
          [523, 659, 784, 1046].forEach((f, i) =>
            tone({
              freq: f,
              durationMs: 200,
              type: 'triangle',
              gain: 0.15,
              startOffsetMs: i * 120,
            }),
          );
          return;
        case 'idle-pop':
          tone({ freq: 440, durationMs: 60, type: 'sine', gain: 0.08 });
          return;
      }
    },
  };
}

// ─── Component ────────────────────────────────────────────────────────────

export function HypeDealWidget({ streamerId, snapshot, fields }: Props) {
  const initial = useRef<GameSnapshot | null>(snapshot);
  const [latest, setLatest] = useState<GameSnapshot | null>(snapshot);
  const sounds = useMemo(() => makeSoundPlayer(), []);
  const prevRef = useRef<GameSnapshot | null>(snapshot);

  useEffect(() => {
    if (initial.current) return;
    const tokenMatch = window.location.search.match(/token=([^&]+)/);
    if (!tokenMatch) return;
    const ac = new AbortController();
    fetch(`/api/hype-deal/snapshot?token=${tokenMatch[1]}`, { signal: ac.signal })
      .then((res) => (res.ok ? res.json() : null))
      .then(
        (json: { snapshot?: GameSnapshot; sound?: typeof DEFAULT_GAME_SOUND_CONFIG } | null) => {
          if (json?.snapshot) setLatest(json.snapshot);
          gameSounds.configure(json?.sound ?? DEFAULT_GAME_SOUND_CONFIG);
        },
      )
      .catch(() => undefined);
    return () => ac.abort();
  }, []);

  useEffect(() => {
    setLatest(snapshot);
  }, [snapshot]);

  // Sound-effect side effects on phase / opens / deal-result transitions.
  // We trigger both the in-widget synth (audible today) and the unified
  // gameSounds player (no-op until real assets are registered, then they
  // layer in). When real packs ship, drop the synth.
  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = latest;
    if (!latest) return;
    if (!prev) {
      sounds.play('idle-pop');
      void gameSounds.play('game_start');
      return;
    }
    // New cases opened?
    if (latest.openedCases.length > prev.openedCases.length) {
      sounds.play('case-open');
      void gameSounds.play('reveal');
    }
    // Phase transitions.
    if (prev.phase !== latest.phase) {
      if (latest.phase === 'banker_offer') {
        sounds.play('phone-ring');
        void gameSounds.play('dramatic_pause');
      } else if (latest.phase === 'deal_voting') {
        void gameSounds.play('suspense_build');
      } else if (latest.phase === 'opening_cases') {
        void gameSounds.play('phase_change');
      } else if (latest.phase === 'game_over' && latest.endState) {
        if (latest.endState.winner === 'deal') {
          sounds.play('deal');
          void gameSounds.play('winner');
        } else if (latest.endState.winner === 'case') {
          sounds.play('no-deal');
          void gameSounds.play(latest.endState.winnings > 0 ? 'winner' : 'loser');
        }
        if (latest.endState.winnings > 0 && latest.endState.perParticipantGems > 0) {
          // Brief delay so the win chime lands after the verbal cue.
          setTimeout(() => {
            sounds.play('game-over-win');
            void gameSounds.play('celebration');
            void gameSounds.play('gems_awarded');
          }, 700);
        }
      }
    }
    // Vote tally bumped — count a vote_received for the unified channel only.
    if (latest.totalVotes > prev.totalVotes) {
      void gameSounds.play('vote_received');
    }
  }, [latest, sounds]);

  void streamerId;
  const opacity = typeof fields.opacity === 'number' ? fields.opacity : 1;
  const scale = typeof fields.scale === 'number' ? fields.scale : 1;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: `linear-gradient(180deg, ${PALETTE.bg} 0%, #03061a 100%)`,
        color: PALETTE.text,
        fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
        opacity,
        transform: scale === 1 ? undefined : `scale(${scale})`,
        transformOrigin: 'top left',
        overflow: 'hidden',
        animation: 'hd-fadeIn 320ms ease both',
      }}
    >
      <style>{ANIMATIONS}</style>
      {!latest ? <IdleStage /> : <Stage snapshot={latest} prev={prevRef.current} />}
    </div>
  );
}

function IdleStage() {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'grid',
        placeItems: 'center',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            fontSize: 96,
            fontWeight: 900,
            letterSpacing: '0.04em',
            background: 'linear-gradient(180deg, #fcd34d 0%, #d97706 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            animation: 'hd-pulse 2400ms ease-in-out infinite',
          }}
        >
          HYPE DEAL
        </div>
        <div style={{ marginTop: 12, fontSize: 24, color: PALETTE.textDim }}>
          Waiting for the streamer to start a round…
        </div>
      </div>
    </div>
  );
}

function Stage({ snapshot, prev }: { snapshot: GameSnapshot; prev: GameSnapshot | null }) {
  // Detect which cases were just opened so we can flip them in.
  const newlyOpened = new Set<number>();
  if (prev) {
    const prevOpened = new Set(prev.openedCases);
    for (const idx of snapshot.openedCases) if (!prevOpened.has(idx)) newlyOpened.add(idx);
  }
  return (
    <>
      <Header snapshot={snapshot} />
      <div
        style={{
          position: 'absolute',
          inset: '120px 40px 40px 40px',
          display: 'grid',
          gridTemplateColumns: '180px 1fr 180px',
          gap: 32,
        }}
      >
        <ValueRail snapshot={snapshot} side="left" />
        <CenterPanel snapshot={snapshot} newlyOpened={newlyOpened} />
        <ValueRail snapshot={snapshot} side="right" />
      </div>
      {snapshot.phase === 'game_over' && snapshot.endState?.winner === 'deal' && <MoneyRain />}
      {snapshot.phase === 'game_over' &&
        snapshot.endState?.winner === 'case' &&
        snapshot.endState.winnings >= 100000 && <Confetti />}
    </>
  );
}

function Header({ snapshot }: { snapshot: GameSnapshot }) {
  const phaseLabel: Record<GameSnapshot['phase'], string> = {
    idle: 'Idle',
    picking_case: 'PICK YOUR CASE',
    opening_cases: `ROUND ${snapshot.currentRound} · OPEN A CASE (${snapshot.casesOpenedThisRound}/${snapshot.casesToOpenThisRound})`,
    banker_offer: "THE BANKER'S OFFER",
    deal_voting: 'DEAL OR NO DEAL?',
    game_over: 'GAME OVER',
  };
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(t);
  }, []);
  const seconds = Math.max(0, Math.ceil((snapshot.phaseEndsAt - now) / 1000));
  return (
    <div
      style={{
        position: 'absolute',
        top: 24,
        left: 40,
        right: 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <div
        style={{
          fontSize: 36,
          fontWeight: 900,
          letterSpacing: '0.02em',
          background: 'linear-gradient(180deg, #fcd34d 0%, #d97706 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}
      >
        HYPE DEAL
      </div>
      <div
        style={{
          fontSize: 32,
          fontWeight: 800,
          letterSpacing: '0.04em',
          color: PALETTE.gold,
        }}
      >
        {phaseLabel[snapshot.phase]}
      </div>
      <div
        style={{
          fontSize: 36,
          fontWeight: 900,
          fontVariantNumeric: 'tabular-nums',
          color: seconds <= 5 && snapshot.phase !== 'game_over' ? PALETTE.red : PALETTE.text,
          animation:
            seconds <= 5 && snapshot.phase !== 'game_over'
              ? 'hd-pulse 600ms ease infinite'
              : undefined,
        }}
      >
        {snapshot.phase === 'game_over' ? '' : `${seconds}s`}
      </div>
    </div>
  );
}

function ValueRail({ snapshot, side }: { snapshot: GameSnapshot; side: 'left' | 'right' }) {
  const allValues = snapshot.valueBoard;
  const half = Math.ceil(allValues.length / 2);
  const list = side === 'left' ? allValues.slice(0, half) : allValues.slice(half);
  return (
    <div
      style={{
        background: PALETTE.bgPanel,
        borderRadius: 14,
        padding: '20px 16px',
        border: `1px solid rgba(245,185,66,0.25)`,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      {list.map(({ value, eliminated }) => (
        <div
          key={value}
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '8px 4px',
            borderRadius: 6,
            fontSize: 22,
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
            color: eliminated ? 'rgba(255,255,255,0.25)' : PALETTE.gold,
            background: eliminated ? 'transparent' : 'rgba(245,185,66,0.08)',
            transition: 'color 360ms ease, background 360ms ease',
            overflow: 'hidden',
          }}
        >
          <span>{fmt(value)}</span>
          {eliminated && (
            <span
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                height: 2,
                background: 'rgba(255,255,255,0.45)',
                animation: 'hd-strike 360ms ease both',
                top: '50%',
                transformOrigin: 'left center',
              }}
            />
          )}
        </div>
      ))}
    </div>
  );
}

function CenterPanel({
  snapshot,
  newlyOpened,
}: {
  snapshot: GameSnapshot;
  newlyOpened: Set<number>;
}) {
  if (snapshot.phase === 'game_over' && snapshot.endState) {
    return <GameOverSplash snapshot={snapshot} />;
  }
  if (snapshot.phase === 'banker_offer' || snapshot.phase === 'deal_voting') {
    return <BankerOfferPanel snapshot={snapshot} />;
  }
  return <CaseBoard snapshot={snapshot} newlyOpened={newlyOpened} />;
}

function CaseBoard({
  snapshot,
  newlyOpened,
}: {
  snapshot: GameSnapshot;
  newlyOpened: Set<number>;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(13, 1fr)',
          gap: 10,
          perspective: '1200px',
        }}
      >
        {snapshot.cases.map((c) => {
          const bg = c.isOpen
            ? PALETTE.caseOpen
            : c.isPlayerCase
              ? PALETTE.casePlayer
              : PALETTE.caseClosed;
          const justOpened = newlyOpened.has(c.index);
          return (
            <div
              key={c.index}
              style={{
                aspectRatio: '4 / 5',
                background: bg,
                borderRadius: 10,
                display: 'grid',
                placeItems: 'center',
                color: c.isOpen ? PALETTE.textDim : '#1a0f00',
                fontWeight: 900,
                fontSize: c.isOpen ? 18 : 28,
                border: c.isPlayerCase ? `3px solid ${PALETTE.gold}` : '1px solid rgba(0,0,0,0.4)',
                boxShadow: c.isOpen ? 'none' : '0 6px 14px rgba(0,0,0,0.5)',
                opacity: c.isOpen ? 0.5 : 1,
                textAlign: 'center',
                padding: 4,
                transformStyle: 'preserve-3d',
                animation: justOpened ? 'hd-flip 720ms ease both' : undefined,
                transition: 'opacity 240ms ease, background 360ms ease',
              }}
            >
              {c.isOpen ? (
                <div
                  style={{
                    lineHeight: 1.1,
                    animation: justOpened ? 'hd-revealValue 480ms ease both 360ms' : undefined,
                  }}
                >
                  <div style={{ fontSize: 14, opacity: 0.6 }}>#{c.index}</div>
                  <div style={{ fontSize: 16, color: PALETTE.gold }}>
                    {c.value !== null ? fmt(c.value) : '?'}
                  </div>
                </div>
              ) : (
                c.index
              )}
            </div>
          );
        })}
      </div>

      <VoteBar snapshot={snapshot} />
    </div>
  );
}

function VoteBar({ snapshot }: { snapshot: GameSnapshot }) {
  if (snapshot.phase === 'picking_case' || snapshot.phase === 'opening_cases') {
    const top = snapshot.topCaseVotes;
    if (top.length === 0) {
      return (
        <div
          style={{
            background: PALETTE.bgPanel,
            borderRadius: 12,
            padding: 16,
            color: PALETTE.textDim,
            textAlign: 'center',
            fontSize: 18,
          }}
        >
          Type a case number 1–26 in chat to vote.
        </div>
      );
    }
    const max = Math.max(...top.map((t) => t.votes), 1);
    return (
      <div
        style={{
          background: PALETTE.bgPanel,
          borderRadius: 12,
          padding: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <div style={{ fontSize: 16, color: PALETTE.textDim, fontWeight: 600 }}>
          TOP VOTES · {snapshot.totalVotes} total
        </div>
        {top.map((t) => (
          <div key={t.caseIndex} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 60, fontSize: 20, fontWeight: 800 }}>#{t.caseIndex}</div>
            <div
              style={{
                flex: 1,
                height: 14,
                borderRadius: 7,
                background: 'rgba(255,255,255,0.08)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${(t.votes / max) * 100}%`,
                  height: '100%',
                  background: PALETTE.gold,
                  transition: 'width 360ms ease',
                }}
              />
            </div>
            <div style={{ width: 40, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
              {t.votes}
            </div>
          </div>
        ))}
      </div>
    );
  }
  return null;
}

function BankerOfferPanel({ snapshot }: { snapshot: GameSnapshot }) {
  const offer = snapshot.bankerOffer ?? 0;
  const { deal, nodeal } = snapshot.dealVotes;
  const total = deal + nodeal || 1;
  const ringingPhone = snapshot.phase === 'banker_offer';
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 32,
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            fontSize: 80,
            marginBottom: 12,
            animation: ringingPhone ? 'hd-shake 700ms ease-in-out infinite' : undefined,
          }}
        >
          ☎️
        </div>
        <div style={{ fontSize: 32, color: PALETTE.textDim, letterSpacing: '0.1em' }}>
          THE BANKER OFFERS
        </div>
        <div
          style={{
            fontSize: 160,
            fontWeight: 900,
            color: PALETTE.gold,
            lineHeight: 1,
            marginTop: 12,
            fontVariantNumeric: 'tabular-nums',
            textShadow: '0 4px 32px rgba(245,185,66,0.5)',
            animation: 'hd-revealValue 520ms ease both',
          }}
        >
          {fmt(offer)}
        </div>
        <div style={{ marginTop: 8, color: PALETTE.textDim, fontSize: 22 }}>
          gems for the whole chat
        </div>
      </div>

      {snapshot.phase === 'deal_voting' && (
        <div style={{ width: '100%', maxWidth: 720 }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16,
              marginBottom: 12,
              fontSize: 28,
              fontWeight: 800,
            }}
          >
            <div style={{ color: PALETTE.green, textAlign: 'center' }}>DEAL · {deal}</div>
            <div style={{ color: PALETTE.red, textAlign: 'center' }}>NO DEAL · {nodeal}</div>
          </div>
          <div
            style={{
              height: 32,
              borderRadius: 16,
              overflow: 'hidden',
              display: 'flex',
              border: '1px solid rgba(255,255,255,0.15)',
            }}
          >
            <div
              style={{
                width: `${(deal / total) * 100}%`,
                background: PALETTE.green,
                transition: 'width 360ms ease',
              }}
            />
            <div
              style={{
                width: `${(nodeal / total) * 100}%`,
                background: PALETTE.red,
                transition: 'width 360ms ease',
              }}
            />
          </div>
          <div
            style={{
              textAlign: 'center',
              marginTop: 16,
              fontSize: 18,
              color: PALETTE.textDim,
            }}
          >
            Tie goes to NO DEAL — chat keeps playing.
          </div>
        </div>
      )}
    </div>
  );
}

function GameOverSplash({ snapshot }: { snapshot: GameSnapshot }) {
  const end = snapshot.endState!;
  const isAborted = end.winner === 'aborted';
  const slowReveal = end.winner === 'case';
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 28,
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        textAlign: 'center',
        animation: 'hd-fadeIn 600ms ease both',
      }}
    >
      <div
        style={{
          fontSize: 56,
          fontWeight: 900,
          color: isAborted ? PALETTE.textDim : PALETTE.gold,
          letterSpacing: '0.06em',
        }}
      >
        {end.winner === 'deal'
          ? 'DEAL ACCEPTED'
          : end.winner === 'case'
            ? 'NO DEAL — FINAL REVEAL'
            : 'GAME ABORTED'}
      </div>
      {!isAborted && (
        <>
          {slowReveal && (
            <div
              style={{
                fontSize: 96,
                animation: 'hd-slowReveal 1200ms ease both',
              }}
            >
              💼
            </div>
          )}
          <div
            style={{
              fontSize: 200,
              fontWeight: 900,
              color: PALETTE.gold,
              lineHeight: 1,
              fontVariantNumeric: 'tabular-nums',
              textShadow: '0 6px 40px rgba(245,185,66,0.55)',
              animation:
                end.winner === 'deal'
                  ? 'hd-glow 1400ms ease-in-out infinite'
                  : 'hd-revealValue 800ms ease both 600ms',
            }}
          >
            {fmt(end.winnings)}
          </div>
          <div style={{ fontSize: 24, color: PALETTE.textDim }}>
            split among {end.participantCount}{' '}
            {end.participantCount === 1 ? 'participant' : 'participants'} ={' '}
            <span style={{ color: PALETTE.text, fontWeight: 700 }}>
              {fmt(end.perParticipantGems)} gems
            </span>{' '}
            each
          </div>
          {end.winner === 'deal' && end.playerCaseValue > 0 && (
            <div style={{ fontSize: 20, color: PALETTE.textDim }}>
              (Chat&apos;s kept case held {fmt(end.playerCaseValue)} gems)
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MoneyRain() {
  const drops = useMemo(
    () =>
      Array.from({ length: 64 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 1.6,
        duration: 1.8 + Math.random() * 1.6,
        symbol: Math.random() < 0.5 ? '💰' : '💵',
        size: 28 + Math.random() * 24,
      })),
    [],
  );
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >
      {drops.map((d) => (
        <div
          key={d.id}
          style={{
            position: 'absolute',
            top: -40,
            left: `${d.left}%`,
            fontSize: d.size,
            animation: `hd-rain ${d.duration}s linear ${d.delay}s infinite`,
          }}
        >
          {d.symbol}
        </div>
      ))}
    </div>
  );
}

function Confetti() {
  const bits = useMemo(
    () =>
      Array.from({ length: 80 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 2,
        duration: 2.4 + Math.random() * 1.4,
        color: ['#f5b942', '#22c55e', '#3b82f6', '#ef4444', '#f5b942'][i % 5],
        rotate: Math.random() * 360,
      })),
    [],
  );
  return (
    <div
      aria-hidden
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}
    >
      {bits.map((b) => (
        <span
          key={b.id}
          style={{
            position: 'absolute',
            top: -20,
            left: `${b.left}%`,
            width: 10,
            height: 16,
            background: b.color,
            transform: `rotate(${b.rotate}deg)`,
            animation: `hd-confetti ${b.duration}s ease-in ${b.delay}s infinite`,
          }}
        />
      ))}
    </div>
  );
}

function fmt(n: number): string {
  return n.toLocaleString();
}
